import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";

import { PortalUserRead } from "../store/coreApi";
import Admin from "./admin/Admin";
import Settings from "./admin/Settings";
import Analysis from "./analysis/Analysis";
import CampaignAnalysis from "./analysis/CampaignAnalysis";
import SpotAnalysis from "./analysis/SpotAnalysis";
import AnnouncementsContainer from "./announcements/AnnouncementsContainer";
import Forbidden from "./authentication/Forbidden";
import Logout from "./authentication/Logout";
import NoAccount from "./authentication/NoAccount";
import NoTenant from "./authentication/NoTenant";
import NotLoggedIn from "./authentication/NotLoggedIn";
import PrivateRoute, { privateRoutePaths } from "./authentication/PrivateRoute";
import CampaignCreateFromCode from "./campaignCreate/CampaignCreateFromCode";
import CampaignDetail from "./campaignDetail/CampaignDetail";
import CampaignContainer from "./campaigns/CampaignContainer";
import CampaignCreateEdit from "./campaigns/CampaignCreateEdit";
import CommercialAudit from "./commercials/audit/CommercialAudit";
import CommercialContainer from "./commercials/CommercialContainer";
import CommercialUpload from "./commercials/CommercialUpload";
import LinkExternalMaterialContainer from "./commercials/create/LinkExternalMaterialContainer";
import DashboardContainer from "./dashboard/DashboardContainer";
import DocumentContainer from "./documents/DocumentContainer";
import InvoiceContainer from "./invoices/InvoiceContainer";
import NotFound from "./NotFound";
import OrganisationContainer from "./organisation/OrganisationContainer";
import ProposalDetails from "./proposal/ProposalDetails";
import UsersContainer from "./users/UsersContainer";

interface AppRoutesProps {
  account?: PortalUserRead;
  settings: { enableAnalysis?: boolean };
}

const DocumentRedirect = () => {
  const { documentId } = useParams();
  window.location.replace(`/api/v1/document/${documentId}`);
  return null;
};

const AppRoutes = ({
  account,
  settings: { enableAnalysis },
}: AppRoutesProps) => (
  <Routes>
    <Route element={<PrivateRoute user={account} />}>
      <Route path="/" element={<DashboardContainer />} />
      <Route path="/index.html" element={<DashboardContainer />} />
      <Route
        path="/campaigns/proposal/:medium/:ids/:from/:to"
        element={<ProposalDetails />}
      />
      {/* Directe link naar documenten via de portal zodat je een melding krijgt als je niet ingelogd bent */}
      <Route path="/documents/:documentId" element={<DocumentRedirect />} />
    </Route>
    <Route
      element={
        <PrivateRoute
          user={account}
          requiredClaims={[
            "campagnes_tv",
            "campagnes_radio",
            "campagnes_inter",
          ]}
        />
      }
    >
      <Route path="/campaigns" element={<CampaignContainer />} />
      <Route path="/campaigns/*" element={<CampaignDetail />}>
        <Route path="/campaigns/*/:medium/:orderId" element={<Outlet />} />
        <Route
          path="/campaigns/*/schedule/:medium/:orderId"
          element={<Outlet />}
        />
        <Route
          path="/campaigns/*/schedule/:medium/:orderId/:subOrderId"
          element={<Outlet />}
        />
        <Route
          path="/campaigns/*/instructions/:medium/:orderId"
          element={<Outlet />}
        />
      </Route>
    </Route>
    {enableAnalysis && (
      <Route
        element={<PrivateRoute user={account} requiredClaims={["analyse"]} />}
      >
        <Route path="/analysis" element={<Analysis />} />
        <Route path="/analysis/campaign" element={<CampaignAnalysis />} />
        <Route path="/analysis/spot" element={<SpotAnalysis />} />
      </Route>
    )}
    <Route
      element={
        <PrivateRoute
          user={account}
          requiredClaims={[
            "aanvragen_formulier_tv",
            "aanvragen_formulier_radio",
            "aanvragen_formulier_inter",
          ]}
        />
      }
    >
      <Route path="/campaigns/new" element={<CampaignCreateEdit />} />
      <Route
        path="/campaigns/new/:medium/:initialRequestId/:from/:to"
        element={<CampaignCreateEdit />}
      />
      <Route
        path="/campaigns/edit/:medium/:orderId/:subOrderId?"
        element={<CampaignCreateEdit isEdit />}
      />
      <Route path="/campaigns/code" element={<CampaignCreateFromCode />} />
      <Route
        path="/campaigns/code/:campaignCode"
        element={<CampaignCreateFromCode />}
      />
    </Route>
    <Route
      element={
        <PrivateRoute
          user={account}
          requiredClaims={["facturen_tv", "facturen_radio", "facturen_inter"]}
        />
      }
    >
      <Route path="/invoices" element={<InvoiceContainer />} />
    </Route>
    <Route
      element={
        <PrivateRoute
          user={account}
          requiredClaims={[
            "materialinzien_tv",
            "materialinzien_radio",
            "materialinzien_inter",
          ]}
        />
      }
    >
      <Route path="/material">
        <Route index element={<CommercialContainer />} />
        <Route path="upload/:medium" element={<CommercialUpload />} />
        <Route path="audit/:medium" element={<CommercialAudit />} />
        <Route path="link" element={<LinkExternalMaterialContainer />} />
      </Route>
    </Route>
    <Route
      element={<PrivateRoute user={account} requiredRoles={["Medewerker"]} />}
    >
      <Route path="/organisation" element={<OrganisationContainer />} />
    </Route>
    <Route
      element={
        <PrivateRoute
          user={account}
          requiredClaims={["documenten_download", "documenten_upload"]}
        />
      }
    >
      <Route path="/documents/*" element={<DocumentContainer />}>
        <Route path="/documents/*/upload" element={<DocumentContainer />} />
      </Route>
    </Route>
    <Route element={<PrivateRoute user={account} requiredRoles={["Admin"]} />}>
      <Route path="/admin" element={<Admin />}>
        <Route
          index
          element={<Navigate to="/admin/users/national" replace />}
        />
        <Route path="users/:tenant" element={<UsersContainer />}>
          <Route path="new" element={<Outlet />} />
          <Route path=":userId" element={<Outlet />} />
        </Route>

        <Route path="announcements" element={<AnnouncementsContainer />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Route>
    <Route path="/logout" element={<Logout />} />
    <Route path="*" element={<NotFound />} />
    <Route path={privateRoutePaths.forbidden} element={<Forbidden />} />
    <Route path={privateRoutePaths.noPortalAccount} element={<NoAccount />} />
    <Route
      path={privateRoutePaths.notLoggedIn}
      element={<NotLoggedIn user={account} />}
    />
    <Route path={privateRoutePaths.noTenant} element={<NoTenant />} />
  </Routes>
);

export default AppRoutes;
